import React, { useState } from 'react';

const C = {
  bg0: '#0B1520', bg1: '#0F1D2C', bg2: '#132030', bg3: '#1A2C40',
  bd: '#1E3A56', tx: '#C8DFF4', dim: '#5A7A9A', dim2: '#3A5A7A',
};

const SECTIONS = [
  {
    label: 'Getting Started',
    color: '#7DC4FF',
    items: [
      {
        q: 'What is PxTracker?',
        a: 'PxTracker is a real-time ward coordination tool for medical teams. It lets you track patients, vital signs, labs, medications, referrals, and tasks — and syncs everything live across your whole team. It runs in your browser, no installation required.',
      },
      {
        q: 'How do I open the app?',
        a: 'Open the URL provided by your team admin in any browser. For the best experience on your phone, add it to your home screen:\n• iPhone/iPad: tap the Share button → "Add to Home Screen"\n• Android: tap the three-dot menu → "Add to Home Screen" or "Install App"\n\nAdding it to your home screen also enables push notifications on iPhone.',
      },
      {
        q: 'What is a shift code / database code?',
        a: 'A shift code groups your team together. Everyone who enters the same shift code sees the same patient list and receives the same notifications. Think of it as a shared workspace — agree on one with your team before starting (e.g. "MICU-AM", "WARD3"). Codes are case-sensitive.',
      },
      {
        q: 'How do I change my shift code?',
        a: 'Tap the ⋯ menu at the top → "Leave Database". You will be brought back to the login screen where you can enter a new code.',
      },
      {
        q: 'Do I need an account or password?',
        a: 'No. Access is controlled entirely by the shift code. Anyone who knows the code can view and edit the patient list, so treat your shift code like a password.',
      },
    ],
  },
  {
    label: 'Patient List',
    color: '#30D158',
    items: [
      {
        q: 'How do I add a new patient?',
        a: 'Tap the + button at the bottom of the screen. Fill in the name, room, age, sex, diagnosis, and urgency status, then tap Save. All team members on the same shift code will see the patient immediately.',
      },
      {
        q: 'What do the status colours mean?',
        a: '• Critical — requires immediate or very close monitoring\n• New — recently admitted, not yet fully assessed\n• Stable — clinically stable\n• HDU — High Dependency Unit, step-down from ICU\n• MGH — May Go Home, awaiting discharge clearance\n• Discharged — moved to the archive\n• Mortality — patient expired, moved to archive',
      },
      {
        q: 'How do I search for a patient?',
        a: 'Use the search bar at the top of the patient list to search by name, room number, or diagnosis.',
      },
      {
        q: 'How do I delete or discharge a patient?',
        a: 'Open the patient\'s chart → tap the Edit button → scroll to the bottom for discharge or delete options. Discharging moves the patient to the archive where they remain accessible. Deleting is permanent.',
      },
    ],
  },
  {
    label: 'Patient Chart — Tabs',
    color: '#FF9500',
    items: [
      {
        q: 'What is the Issues tab for?',
        a: 'The Issues tab has three subtabs:\n• Issues — the active problem list, resolved issues, endorsement notes, and allergy display\n• History — full clinical history (CC, HPI, PMH, FH, OB-GYN, PSH, ROS, PE) with Paste History parser\n• Surgery — surgical and procedural log with status, dates, and clearances',
      },
      {
        q: 'How does "Paste History" work?',
        a: 'In Issues → History, tap "📋 Paste History" and paste any typed admission note. The app recognises common section headers (CC:, HPI:, PMH:, Allergies:, PE:, VS:, etc.) and automatically fills the correct fields. Text is fully editable after.',
      },
      {
        q: 'What is the Notes tab?',
        a: 'The Notes tab is for SOAP progress notes — the daily clinical narrative. Tap "+ New Progress Note" to write one. Fields: Physician, Subjective (S), Objective (O), Assessment (A), Plan (P).\n\nTap "⚡ Auto-fill" next to Objective to pull the latest vitals and flagged labs. Tap "⚡ Auto-fill" next to Assessment to pull the active issues list. Both are editable after filling.',
      },
      {
        q: 'How does the Vitals tab work?',
        a: 'Enter BP, HR, RR, Temperature, SpO₂, AVPU, and GCS (E/V/M). NEWS2 score and total GCS are calculated automatically and colour-coded. Trend arrows show whether values are improving or worsening vs. the previous entry. I/O (Intake and Output) tracking is at the bottom.',
      },
      {
        q: 'How do I add labs?',
        a: 'In the Labs tab, tap "+ Add Labs" to enter individual results, or tap "Paste Labs" to paste a full lab printout — the parser recognises over 80 common lab names and fills values automatically. Flag results as High, Low, or Critical to highlight them. Flagged results appear in the Ward Round summary.',
      },
      {
        q: 'What are the Studies subtabs?',
        a: 'Studies has six subtabs: Cardiology (ECG, 2D Echo), Radiology (X-Ray, CT, MRI, Ultrasound), Pathology (cultures — Blood, Urine, Wound, Sputum, CSF — with result, organism, sensitivity), Nuclear Med, Pulmonology, and Others.',
      },
      {
        q: 'How do I update a culture result?',
        a: 'In Studies → Pathology, tap the result status badge on any entry (Pending / No Growth / Positive / Contaminated) to update it inline. You can also update the organism and sensitivity/resistance fields directly.',
      },
    ],
  },
  {
    label: 'Ward Round Mode',
    color: '#BF5AF2',
    items: [
      {
        q: 'How do I start a ward round?',
        a: 'Tap the 🔄 icon on the patient list, or go to the ⋯ menu → Start Rounds. You can filter by team, subspecialty, or urgency before starting. The app steps through each patient one at a time.',
      },
      {
        q: 'What does each patient card show during rounds?',
        a: 'Urgency status, latest vitals, NEWS2 score, GCS, active issues, flagged labs, and a summary of everything that changed in the last 24 hours — new tasks, labs, vitals, notes, and referrals.',
      },
      {
        q: 'Can I write a note during the ward round?',
        a: 'Yes. Tap "+ Add Note" on any patient card to write a SOAP note directly from the ward round screen. Notes sync automatically to the patient\'s Notes tab.',
      },
      {
        q: 'How do I mark a patient as reviewed?',
        a: 'Tap "Next Patient →" to mark the current patient as seen and move to the next one. Tap "✓ Finish Round" at the last patient to end the session.',
      },
    ],
  },
  {
    label: 'Notifications',
    color: '#FF453A',
    items: [
      {
        q: 'How do I enable notifications?',
        a: 'Tap Allow when the browser prompts you. If you missed it:\n• iPhone: Settings → Safari → find the app → Notifications → Allow\n• Android: Settings → Apps → your browser → Notifications → Allow\n• On iPhone, you must add the app to your Home Screen — notifications don\'t work in regular Safari.',
      },
      {
        q: 'What events trigger a notification?',
        a: 'New patient added, status change, discharged/mortality, new or updated tasks, new vitals, new labs, new study results, culture updates, new referral, referral status change, and progress notes saved.',
      },
      {
        q: 'I allowed notifications but I\'m not receiving them.',
        a: 'Check: (1) Is your phone in Do Not Disturb or Focus mode? (2) Are notifications enabled for the browser app in System Settings? (3) On iPhone, is the app added to the Home Screen? (4) If all clear, ask your admin to check the notification server logs.',
      },
      {
        q: 'Can I control which notifications I receive?',
        a: 'Yes. Tap the ⋯ menu → Notifications to filter by team, subspecialty, or service. You will only receive notifications for patients matching your filter.',
      },
    ],
  },
  {
    label: 'Syncing & Data',
    color: '#64D2FF',
    items: [
      {
        q: 'Is patient data encrypted?',
        a: 'Yes. All data is encrypted in transit (HTTPS/TLS) and at rest via Google Firebase. Your database is only accessible to people who know both the database code and PIN — no account or login is required from our side. For additional security, rotate your database code and PIN regularly.',
      },
      {
        q: 'Does the app work offline?',
        a: 'The app loads from cache if you\'ve visited before, but changes won\'t sync to the team while offline. Stay connected for real-time collaboration.',
      },
      {
        q: 'What does "Save Error — tap to retry" mean?',
        a: 'The app couldn\'t reach the database to save your changes. This is usually a brief network issue. Tap the error bar to retry — the app will resend any unsaved changes. Your data is not lost.',
      },
      {
        q: 'Is patient data stored on my phone?',
        a: 'A local cache is kept for faster loading, but all records are stored in the cloud database. Clearing your browser cache or removing the app from your home screen does not delete patient data.',
      },
      {
        q: 'What happens to data when a shift ends?',
        a: 'Data stays in the database under the shift code indefinitely. The incoming team can use the same code to access all history. Many teams rotate codes monthly (e.g. "WARD3-April") to keep things organised.',
      },
      {
        q: 'Can multiple teams use the app at the same time?',
        a: 'Yes. Different shift codes are completely separate workspaces. Team A and Team B each see only their own patient list, even on the same app URL.',
      },
    ],
  },
  {
    label: 'Printing & Export',
    color: '#A8D878',
    items: [
      {
        q: 'How do I print a patient summary?',
        a: 'Open the patient\'s chart and tap the print icon at the top right. This generates a formatted summary with vitals, labs, studies, SOAP notes, medications, tasks, and referrals. Print from your browser as usual.',
      },
      {
        q: 'How do I print the ward list?',
        a: 'From the main patient list, tap the print icon to generate a one-page ward list with all patients, their status, room, diagnosis, and key flags.',
      },
      {
        q: 'How do I back up patient data?',
        a: 'Tap the ⋯ menu → "Copy Full Backup". This copies a JSON snapshot of all patients to your clipboard, which you can paste into a notes app or document for safekeeping.',
      },
    ],
  },
];

function FAQItem({ item }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ borderBottom: `1px solid ${C.bd}` }}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          width: '100%', background: 'none', border: 'none', cursor: 'pointer',
          padding: '14px 0', display: 'flex', alignItems: 'flex-start', gap: 12,
          textAlign: 'left',
        }}
      >
        <span style={{
          fontSize: 11, fontWeight: 800, color: open ? '#7DC4FF' : C.dim,
          marginTop: 1, flexShrink: 0, transition: 'transform 0.2s',
          display: 'inline-block', transform: open ? 'rotate(90deg)' : 'rotate(0deg)',
        }}>▶</span>
        <span style={{ fontSize: 14, fontWeight: open ? 700 : 500, color: open ? '#E0EEFA' : C.tx, lineHeight: 1.4, flex: 1 }}>
          {item.q}
        </span>
      </button>
      {open && (
        <div style={{ paddingLeft: 23, paddingBottom: 16 }}>
          {item.a.split('\n').map((line, i) => (
            <p key={i} style={{ fontSize: 13, color: C.dim, lineHeight: 1.75, marginBottom: line === '' ? 0 : 4 }}>
              {line}
            </p>
          ))}
        </div>
      )}
    </div>
  );
}

export default function FAQView({ onClose }) {
  const [search, setSearch] = useState('');
  const q = search.trim().toLowerCase();

  const filtered = q
    ? SECTIONS.map(s => ({
        ...s,
        items: s.items.filter(
          item => item.q.toLowerCase().includes(q) || item.a.toLowerCase().includes(q)
        ),
      })).filter(s => s.items.length > 0)
    : SECTIONS;

  return (
    <div style={{ position: 'fixed', inset: 0, background: C.bg0, zIndex: 600, display: 'flex', flexDirection: 'column', overflowY: 'auto' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, paddingTop: 'max(16px, env(safe-area-inset-top, 16px))', paddingBottom: 12, paddingLeft: 'max(16px, env(safe-area-inset-left, 16px))', paddingRight: 'max(16px, env(safe-area-inset-right, 16px))', background: C.bg1, borderBottom: `1px solid ${C.bd}`, flexShrink: 0, position: 'sticky', top: 0, zIndex: 10 }}>
        <button onClick={onClose} style={{ background: 'none', border: 'none', color: C.dim, fontSize: 20, cursor: 'pointer', lineHeight: 1, padding: 4 }}>←</button>
        <div>
          <div style={{ fontSize: 17, fontWeight: 800, color: '#E0EEFA' }}>Help & FAQ</div>
          <div style={{ fontSize: 11, color: C.dim, marginTop: 1 }}>Tap any question to expand</div>
        </div>
      </div>

      {/* Search */}
      <div style={{ padding: '12px 16px', background: C.bg1, borderBottom: `1px solid ${C.bd}`, flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', background: C.bg3, border: `1px solid ${C.bd}`, borderRadius: 10, padding: '8px 12px', gap: 8 }}>
          <span style={{ fontSize: 14, color: C.dim2 }}>🔍</span>
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search questions…"
            style={{ flex: 1, background: 'none', border: 'none', outline: 'none', color: C.tx, fontSize: 14, fontFamily: 'inherit' }}
          />
          {search && (
            <button onClick={() => setSearch('')} style={{ background: 'none', border: 'none', color: C.dim, cursor: 'pointer', fontSize: 16, lineHeight: 1 }}>×</button>
          )}
        </div>
      </div>

      {/* Content */}
      <div style={{ flex: 1, padding: '8px 16px', paddingBottom: 'max(40px, env(safe-area-inset-bottom, 40px))' }}>
        {filtered.length === 0 && (
          <div style={{ textAlign: 'center', color: C.dim, fontSize: 14, paddingTop: 48 }}>
            No results for "{search}"
          </div>
        )}
        {filtered.map(section => (
          <div key={section.label} style={{ marginBottom: 24 }}>
            {/* Section header */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, paddingTop: 20, paddingBottom: 8, position: 'sticky', top: 0, background: C.bg0, zIndex: 5 }}>
              <div style={{ width: 3, height: 16, borderRadius: 2, background: section.color, flexShrink: 0 }} />
              <span style={{ fontSize: 11, fontWeight: 800, color: section.color, textTransform: 'uppercase', letterSpacing: '0.1em' }}>{section.label}</span>
            </div>
            <div style={{ background: C.bg1, border: `1px solid ${C.bd}`, borderRadius: 12, padding: '0 16px' }}>
              {section.items.map((item, i) => (
                <FAQItem key={i} item={item} />
              ))}
            </div>
          </div>
        ))}

        {/* Footer note */}
        {!q && (
          <div style={{ textAlign: 'center', color: C.dim2, fontSize: 12, paddingTop: 12, paddingBottom: 8, lineHeight: 1.6 }}>
            PxTracker is a ward coordination tool.<br />It does not replace your hospital's official EMR.
          </div>
        )}
      </div>
    </div>
  );
}
